import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import EsgEvaluationWorkspace from './EsgEvaluationWorkspace';
import { getWeekLabel } from '../../utils/sessionUtils';

// draft = nothing submitted yet, partial = some of the squad / chair / task steps submitted, approved = every step done.
const STATUS_PILLS = {
  approved: { label: 'Complete', className: 'status-pill completed', style: undefined },
  partial: { label: 'In progress', className: 'status-pill active', style: { background: '#e0e7ff', color: '#3730a3' } },
  draft: { label: 'Not started', className: 'status-pill active', style: { background: '#f3f4f6', color: '#4b5563' } },
  none: { label: 'Not evaluated', className: 'status-pill active', style: { background: '#fef3c7', color: '#d97706' } },
};

const formatTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const formatDay = (iso) => iso.split('T')[0];
const levelNumber = (level) => (level?.code || '').replace(/\D/g, '') || null;

export const squadDetailHash = (squadId) => `#/esg/squad/${squadId}`;

// "My Sessions"-style list: one card per scheduled council session of every squad the user may evaluate
// (any squad for a Program Administrator, own squads for a SPOC, assigned squads for an auditor).
export default function EsgEvaluation({ onError, onSuccess }) {
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('past');
  const [open, setOpen] = useState(null); // { squadId, council }

  const loadSquads = useCallback(async () => {
    try {
      setSquads(await mentorApi.getEsgEvaluationSquads());
    } catch (err) {
      onError?.("Couldn't load sessions - try again.");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    loadSquads();
  }, [loadSquads]);

  const sessions = useMemo(() => {
    const rows = [];
    squads.forEach((squad) => {
      squad.councils.forEach((c) => {
        if (!c.scheduled_at) return; // nothing to show until the chair has scheduled it
        rows.push({ ...c, key: `${squad.id}:${c.council_number}`, squad });
      });
    });
    return rows;
  }, [squads]);

  const upcoming = sessions.filter((s) => !s.started).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const past = sessions.filter((s) => s.started).sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  const current = tab === 'upcoming' ? upcoming : past;

  const grouped = useMemo(() => {
    const groups = {};
    current.forEach((s) => {
      const label = getWeekLabel(s.scheduled_at);
      (groups[label] = groups[label] || []).push(s);
    });
    return groups;
  }, [current]);

  if (open) {
    return (
      <EsgEvaluationWorkspace
        squadId={open.squadId}
        council={open.council}
        backLabel="Back to Evaluation"
        onBack={() => { setOpen(null); loadSquads(); }}
        onChanged={loadSquads}
        onError={onError}
        onSuccess={onSuccess}
      />
    );
  }

  const renderCard = (s) => {
    const pill = STATUS_PILLS[s.evaluation_status || 'none'];
    const openWorkspace = () => setOpen({ squadId: s.squad.id, council: s.council_number });
    const lvl = levelNumber(s.squad.level);

    return (
      <div className="timeline-session-card" key={s.key}>
        <div className="session-card-header">
          <div>
            <div className="esg-evaluation__div-1">
              <span className="session-card-type">{`Council ${s.council_number}`}</span>
              {lvl && <span className="year-level-badge year-1">{`Level ${lvl}`}</span>}
              {s.is_revision && <span className="role-badge revised">Revised</span>}
            </div>
            <h3 className="esg-evaluation__h3-2">
              <a href={squadDetailHash(s.squad.id)} className="esg-evaluation__a-3">{s.squad.name}</a>
            </h3>
            <p className="esg-evaluation__p-4">🌍 {s.squad.level?.name || 'ESG Specialist'}</p>
          </div>
          {s.started && <span className={pill.className} style={pill.style}>{pill.label}</span>}
        </div>

        <div className="session-card-meta esg-evaluation__div-5">
          <div>📅 Date: <strong>{formatDay(s.scheduled_at)}</strong></div>
          <div className="esg-evaluation__div-6">🕒 Time: <strong>{formatTime(s.scheduled_at)}</strong></div>
          <div className="esg-evaluation__div-6">👥 Members: <strong>{(s.squad.members || []).length}</strong></div>
          {/* The chair's online link for the session, entered in LMS. Only an https address is ever made clickable. */}
          {s.meeting_url && s.meeting_url.startsWith('https://') ? (
            <div className="esg-evaluation__div-6">
              🔗 Online link:{' '}
              <a href={s.meeting_url} target="_blank" rel="noopener noreferrer" className="esg-evaluation__a-7">Join session</a>
            </div>
          ) : !s.started && (
            <div className="esg-evaluation__div-8">🔗 Online link: not added yet</div>
          )}
          {s.session_rating !== null && s.session_rating !== undefined && (
            <div className="esg-evaluation__div-6">⭐ Ratings (session / chair): <strong>{`${s.session_rating} / ${s.chair_rating ?? '—'}`}</strong></div>
          )}
        </div>

        {s.started && (
          <div className="session-card-footer esg-evaluation__div-5">
            <div className="esg-evaluation__div-9">
              <button type="button" className="esg-btn esg-btn-secondary esg-evaluation__button-10" onClick={openWorkspace}>
                {s.evaluation_status === 'partial' ? 'Continue Evaluation' : s.evaluation_status === 'approved' ? 'View Evaluation' : 'Evaluate'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="auditor-sessions-container animate-fade-in">
      <div className="dashboard-title-row">
        <div>
          <h1 className="dashboard-title">ESG Evaluation</h1>
          <p className="dashboard-subtitle">Council sessions, evaluations, and past submissions.</p>
        </div>
      </div>

      <div className="auditor-tab-row esg-evaluation__div-11">
        <button type="button" className={`auditor-tab-button ${tab === 'upcoming' ? 'active' : ''}`} onClick={() => setTab('upcoming')}>
          {`Upcoming Sessions (${upcoming.length})`}
        </button>
        <button type="button" className={`auditor-tab-button ${tab === 'past' ? 'active' : ''}`} onClick={() => setTab('past')}>
          {`Past Sessions (${past.length})`}
        </button>
      </div>

      {loading ? (
        <div className="empty-state-card esg-evaluation__div-12"><p className="empty-state-description">Loading sessions…</p></div>
      ) : current.length === 0 ? (
        <div className="empty-state-card esg-evaluation__div-12">
          <h3 className="empty-state-heading">{tab === 'upcoming' ? 'No upcoming sessions' : 'No past sessions'}</h3>
          <p className="empty-state-description">
            {squads.length === 0 ? 'No squads are assigned to you yet.' : 'Sessions appear here once a squad chair schedules them.'}
          </p>
        </div>
      ) : (
        Object.keys(grouped).map((weekLabel) => (
          <div key={weekLabel} className="timeline-week-group">
            <div className="timeline-week-header"><span>{weekLabel}</span></div>
            <div className="timeline-grid">{grouped[weekLabel].map(renderCard)}</div>
          </div>
        ))
      )}
    </div>
  );
}
